import { useEffect, useState } from 'react';
import { ApiError, getConfig } from '../api';
import { Footer, TopBar } from '../components/TopBar';
import { Turnstile } from '../components/Turnstile';
import { HONEYPOT_FIELD, HONEYPOT_HIDDEN_STYLE } from '../honeypot';

interface RegionRow {
  id: string;
  name: string;
  radius_mi: number;
  open: { top: boolean; second: boolean };
}

interface RateCard {
  prices: { top_cents: number; second_cents: number };
  regions: RegionRow[];
}

const money = (cents: number) => `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

const EMPTY_FORM = {
  region_id: '',
  slot: 'top' as 'top' | 'second',
  name: '',
  headline: '',
  body: '',
  destination_url: '',
  contact_name: '',
  contact_email: '',
};

/**
 * Direct-sold, privacy-respecting display ads (owner decision, 2026-09-25/26): no ad network, no
 * third-party tracker. A business picks an open region and slot here, pays a monthly Stripe
 * subscription, and keeps that slot exclusively for as long as it keeps paying. Every purchase
 * is held for review before it shows to shoppers (owner decision, 2026-09-27) -- paying reserves
 * the slot; it doesn't skip a human look at the artwork and copy.
 */
export function Advertise() {
  const [card, setCard] = useState<RateCard | null>(null);
  const [siteKey, setSiteKey] = useState<string | null | undefined>(undefined);
  const [token, setToken] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creative, setCreative] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [cancelled] = useState(() => new URLSearchParams(location.search).get('checkout') === 'cancelled');

  useEffect(() => {
    document.title = 'Advertise — The Yard Sale Map';
    fetch('/api/v1/ad-regions')
      .then((r) => r.json())
      .then(setCard)
      .catch(() => setCard({ prices: { top_cents: 0, second_cents: 0 }, regions: [] }));
    getConfig()
      .then((c) => setSiteKey(c.turnstileSiteKey))
      .catch(() => setSiteKey(null));
  }, []);

  const region = card?.regions.find((r) => r.id === form.region_id) ?? null;
  const openSlotsFor = (r: RegionRow) => (['top', 'second'] as const).filter((s) => r.open[s]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr('');
    if (siteKey && !token) {
      setErr('Please complete the “verify you are human” check above the button.');
      return;
    }
    if (!form.region_id) {
      setErr('Choose a region.');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      for (const [k, v] of Object.entries(form)) fd.append(k, v);
      fd.append('cf-turnstile-response', token);
      fd.append(HONEYPOT_FIELD, '');
      if (creative) fd.append('creative', creative);
      const res = await fetch('/api/v1/ad-checkout', { method: 'POST', body: fd });
      const body = (await res.json()) as { checkout_url?: string | null; error?: { message: string } };
      if (!res.ok || !body.checkout_url) throw new ApiError(res.status, body.error?.message ?? 'Something went wrong', 'error', []);
      location.href = body.checkout_url;
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : 'Something went wrong starting checkout. Please try again.');
      setResetKey((k) => k + 1);
      setBusy(false);
    }
  }

  return (
    <>
      <TopBar />
      <main className="page" id="main">
        <h1>Advertise your business</h1>
        <p>
          A sponsored spot in search results near your customers — never sold to an ad network, never used to
          track shoppers across the web. One business holds each spot at a time, for as long as it keeps
          paying, so your ad is never crowded out by a competitor buying the same region.
        </p>
        {cancelled && <p className="hint">Checkout was cancelled — nothing was charged.</p>}

        {!card ? (
          <p aria-live="polite">Loading…</p>
        ) : card.regions.length === 0 ? (
          <p>No regions are set up for advertising yet. Email <strong>info@theyardsalemap.com</strong> and we'll let you know when one opens near you.</p>
        ) : (
          <>
            <h2>Rate card</h2>
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Region</th>
                    <th>Top spot — {money(card.prices.top_cents)}/mo</th>
                    <th>Second spot — {money(card.prices.second_cents)}/mo</th>
                  </tr>
                </thead>
                <tbody>
                  {card.regions.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <strong>{r.name}</strong>
                        <div className="hint">
                          {r.radius_mi} mi radius
                        </div>
                      </td>
                      <td>{r.open.top ? 'Open' : 'Taken'}</td>
                      <td>{r.open.second ? 'Open' : 'Taken'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h2>Get a spot</h2>
            <form onSubmit={(e) => void submit(e)}>
              <label htmlFor="ad-region">Region</label>
              <select
                id="ad-region"
                value={form.region_id}
                onChange={(e) => setForm({ ...form, region_id: e.target.value, slot: openSlotsFor(card.regions.find((r) => r.id === e.target.value)!)[0] ?? 'top' })}
                required
              >
                <option value="">Choose a region…</option>
                {card.regions
                  .filter((r) => r.open.top || r.open.second)
                  .map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name} ({r.radius_mi} mi radius)
                    </option>
                  ))}
              </select>
              {region && (
                <>
                  <label htmlFor="ad-slot">Spot</label>
                  <select id="ad-slot" value={form.slot} onChange={(e) => setForm({ ...form, slot: e.target.value as 'top' | 'second' })}>
                    {region.open.top && <option value="top">Top — {money(card.prices.top_cents)}/month</option>}
                    {region.open.second && <option value="second">Second — {money(card.prices.second_cents)}/month</option>}
                  </select>
                </>
              )}
              <label htmlFor="ad-name">Business name</label>
              <input id="ad-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={120} />
              <label htmlFor="ad-headline">Headline</label>
              <input id="ad-headline" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} required maxLength={120} />
              <label htmlFor="ad-body">Short description (optional)</label>
              <input id="ad-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} maxLength={300} />
              <label htmlFor="ad-link">Link (http/https)</label>
              <input id="ad-link" value={form.destination_url} onChange={(e) => setForm({ ...form, destination_url: e.target.value })} placeholder="https://" required />
              <label htmlFor="ad-creative">Artwork (optional — image, GIF or a video up to 60s)</label>
              <input
                id="ad-creative"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
                onChange={(e) => setCreative(e.target.files?.[0] ?? null)}
              />
              <p className="hint">No artwork yet? You can email it to info@theyardsalemap.com afterward.</p>
              <label htmlFor="ad-contact-name">Your name</label>
              <input id="ad-contact-name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} required maxLength={120} />
              <label htmlFor="ad-contact-email">Your email</label>
              <input
                id="ad-contact-email"
                type="email"
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                required
              />
              {/* honeypot -- hidden from people, irresistible to bots */}
              <div aria-hidden="true" style={HONEYPOT_HIDDEN_STYLE}>
                <label htmlFor="ad-fax">Fax number</label>
                <input id="ad-fax" name={HONEYPOT_FIELD} tabIndex={-1} autoComplete="off" />
              </div>
              {siteKey !== undefined && <Turnstile siteKey={siteKey} onToken={setToken} resetKey={resetKey} />}
              <p className="hint">
                Your card is charged monthly by Stripe once you continue. Your spot is reserved right away, but
                shows to shoppers only after we review it — usually within a day. Cancel any time by emailing us.
              </p>
              {err && (
                <p className="error" role="alert">
                  {err}
                </p>
              )}
              <button className="primary" type="submit" disabled={busy || !form.region_id}>
                Continue to payment
              </button>
            </form>
          </>
        )}

        <p style={{ marginTop: 24 }}>
          Questions first? Email <strong>info@theyardsalemap.com</strong>.
        </p>
      </main>
      <Footer />
    </>
  );
}
